import { type Dispatch, type SetStateAction } from 'react'

import {
  type CollaborationSettingsGroupRole,
  type CollaborationState,
  type CollaborationTerms,
} from '../graphql/generated'
import { type RemoteFile, type RemoteImage } from '../utils/storage'
import { type CollaborationSettings, type Group } from './GroupProvider'
import { type PublicTrack, type Track } from './TrackProvider.types'

export interface CollaborationTrack extends PublicTrack {
  state?: CollaborationState
}

export interface CollaborationsContext {
  activeFilters: Filters
  clearFilters: () => void
  collaborationNextActions: {
    label: string
    value: string
  }[]
  collaborationStates: string[]
  collaborations: Collaboration[]
  error: boolean
  hasActiveFilters: boolean
  loading: boolean
  setActiveFilters: Dispatch<SetStateAction<Filters>>
}

export interface Filters {
  nextAction: string
  status: string | string[]
}

export interface CreatedByGroup {
  avatar: RemoteImage
  id: string
  name: string
}

export interface Post {
  id: string
  platform: Track
  proofOfDeliveries:
    | {
        id: string
        status: string
      }[]
    | null
}

export interface To {
  avatar: RemoteFile
  name: string
}

export interface Term {
  acceptedAt: string
  collaborationEndDate: string
  collaborationStartDate: string
  createdAt: string
  createdByGroup: CreatedByGroup
  declinedAt: string
  fixedFee: number
  id: string
  message: string
  order: CollaborationTerms['order']
  paymentEndDate: string | null
  paymentStartDate: string | null
  posts: Post[]
  postsType?: string
  royaltySharePercentage: number
  track: Track
  type: string
  version: number
}

export interface Collaboration {
  canceledAt: string
  createdAt: string
  from: Group
  id?: string
  state: CollaborationState
  terms: Term[]
  to: Group
}

export interface Participant {
  collaborationSettings: CollaborationSettings
  profile: Group
}

export interface Participants {
  artist?: Participant
  influencer?: Participant
  loaded?: boolean
  myGroupId?: string
  myGroupRole?: CollaborationSettingsGroupRole
  otherGroupId?: string
  otherGroupRole?: CollaborationSettingsGroupRole
}

export interface AllCollaborationStates {
  approvalState?: CollaborationApprovalState
  collaborationDateState?: DateState
  collaborationState: CollaborationState
  collaborationType?: CollaborationType
  paymentDateState?: DateState
  paymentState?: CollaborationPaymentState
  proofState?: CollaborationProofState
}

export enum CollaborationType {
  fixedFee = 'fixedFee',
  royaltyShare = 'royaltyShare',
}

export enum CollaborationNextAction {
  awaitingDraft = 'AWAITING_DRAFT',
  awaitingFeedback = 'AWAITING_FEEDBACK',
  awaitingPayment = 'AWAITING_PAYMENT',
  awaitingProof = 'AWAITING_PROOF',
  awaitingReview = 'AWAITING_REVIEW',
  deliverProof = 'DELIVER_PROOF',
  error = 'ERROR',
  none = 'NONE',
  paymentPending = 'PAYMENT_PENDING',
  paymentRequired = 'PAYMENT_REQUIRED',
  provideDraft = 'PROVIDE_DRAFT',
  provideFeedback = 'PROVIDE_FEEDBACK',
  reviewCollab = 'REVIEW_COLLAB',
  reviewProof = 'REVIEW_PROOF',
}

export enum CollaborationPaymentState {
  completed = 'completed',
  none = 'none',
  pending = 'pending',
}

export enum CollaborationApprovalState {
  accepted = 'ACCEPTED',
  canceled = 'CANCELED',
  declined = 'DECLINED',
  proposed = 'PROPOSED',
}

export enum CollaborationProofState {
  accepted = 'ACCEPTED',
  awaiting = 'AWAITING',
  delivered = 'DELIVERED',
}

export enum DateState {
  ongoing = 'ONGOING',
  passed = 'PASSED',
  upcoming = 'UPCOMING',
}

export enum PostFrequencyPeriod {
  monthly = 'monthly',
  weekly = 'weekly',
  yearly = 'yearly',
}

export interface CollaborationPostInput {
  platformId: string
}

export interface CollaborationCompensationInput {
  fixedFee?: number
  paymentEndDate?: string
  paymentStartDate?: string
  royaltySharePercentage?: number
}

export interface CollaborationCounterProposalInput {
  collaborationEndDate?: string
  collaborationStartDate?: string
  compensation: CollaborationCompensationInput
  createdByGroupId: string
  message?: string
  postAmount: number
  postFrequency?: number
  postPeriod?: PostFrequencyPeriod
  posts: CollaborationPostInput[]
  postsType?: string
  trackId: string
  type: CollaborationType
}
